import { Box, Grow, Stack } from '@mui/material';
import { ReportsProjectFilter } from 'src/@types/project';
import { Teammate } from 'src/@types/teammates';
import { formatUserName } from 'src/utils/formatUserName';
import { FilterLabel } from './FilterLabel';
import formatDateRange from './formatDateRange';
import { Range } from 'src/components/date-range-picker';
import { ReportsGeographyFilter } from 'src/@types/geographyFilters';
import { ReportsTeamFilter } from 'src/@types/team';
import { TOutreachType, formatOutreachType } from 'src/@types/outreachType';

type ReportsFiltersProps = {
  setFilteredUser?: (user: Teammate | Partial<Teammate> | null) => void;
  filteredUser?: Teammate | Partial<Teammate> | null;
  setFilteredProject?: (project: ReportsProjectFilter | null) => void;
  filteredProject?: ReportsProjectFilter | null;
  setFilteredDateRange?: (daterange: Range | null) => void;
  filteredDateRange?: Range | null;
  setFilteredCounty?: (county: ReportsGeographyFilter | null) => void;
  filteredCounty?: ReportsGeographyFilter | null;
  setFilteredPrecinct?: (precinct: ReportsGeographyFilter | null) => void;
  filteredPrecinct?: ReportsGeographyFilter | null;
  setFilteredCongressionalDistrict?: (congressionalDistrict: ReportsGeographyFilter | null) => void;
  filteredCongressionalDistrict?: ReportsGeographyFilter | null;
  setFilteredStateHouseDistrict?: (stateHouseDistrict: ReportsGeographyFilter | null) => void;
  filteredStateHouseDistrict?: ReportsGeographyFilter | null;
  setFilteredStateSenateDistrict?: (stateSenateDistrict: ReportsGeographyFilter | null) => void;
  filteredStateSenateDistrict?: ReportsGeographyFilter | null;
  setFilteredDma?: (dma: ReportsGeographyFilter | null) => void;
  filteredDma?: ReportsGeographyFilter | null;
  setFilteredTeam?: (team: ReportsTeamFilter | null) => void;
  filteredTeam?: ReportsTeamFilter | null;
  setFilteredOutreachType?: (outreachType: TOutreachType | null) => void | null;
  filteredOutreachType?: TOutreachType | null;
};

export default function ReportsFiltersChips({
  setFilteredUser,
  filteredUser,
  setFilteredProject,
  filteredProject,
  setFilteredDateRange,
  filteredDateRange,
  setFilteredCounty,
  filteredCounty,
  setFilteredPrecinct,
  filteredPrecinct,
  setFilteredCongressionalDistrict,
  filteredCongressionalDistrict,
  setFilteredStateHouseDistrict,
  filteredStateHouseDistrict,
  setFilteredStateSenateDistrict,
  filteredStateSenateDistrict,
  setFilteredDma,
  filteredDma,
  setFilteredTeam,
  filteredTeam,
  setFilteredOutreachType,
  filteredOutreachType,
}: ReportsFiltersProps) {
  return (
    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5} sx={{ marginTop: 0.5 }}>
      {filteredOutreachType && setFilteredOutreachType && (
        <Grow in={!!filteredOutreachType}>
          <Box>
            <FilterLabel
              onDelete={() => setFilteredOutreachType(null)}
              type="outreach"
              value={formatOutreachType(filteredOutreachType)}
            />
          </Box>
        </Grow>
      )}
      {filteredUser && setFilteredUser && (
        <Grow in={!!filteredUser}>
          <Box>
            <FilterLabel
              onDelete={() => setFilteredUser(null)}
              type="user"
              value={formatUserName(filteredUser.first_name || '', filteredUser.last_name || '')}
            />
          </Box>
        </Grow>
      )}
      {filteredTeam && setFilteredTeam && (
        <Grow in={!!filteredTeam}>
          <Box>
            <FilterLabel
              onDelete={() => setFilteredTeam(null)}
              type="team"
              value={filteredTeam.name}
            />
          </Box>
        </Grow>
      )}
      {filteredProject && setFilteredProject && (
        <Grow in={!!filteredProject}>
          <Box>
            <FilterLabel
              onDelete={() => setFilteredProject(null)}
              type="project"
              value={filteredProject.name}
            />
          </Box>
        </Grow>
      )}
      {filteredCounty && setFilteredCounty && (
        <Grow in={!!filteredCounty}>
          <Box>
            <FilterLabel
              onDelete={() => setFilteredCounty(null)}
              type="county_name"
              value={filteredCounty.name}
            />
          </Box>
        </Grow>
      )}
      {filteredPrecinct && setFilteredPrecinct && (
        <Grow in={!!filteredPrecinct}>
          <Box>
            <FilterLabel
              onDelete={() => setFilteredPrecinct(null)}
              type="precinct_name"
              value={filteredPrecinct.name}
            />
          </Box>
        </Grow>
      )}
      {filteredCongressionalDistrict && setFilteredCongressionalDistrict && (
        <Grow in={!!filteredCongressionalDistrict}>
          <Box>
            <FilterLabel
              onDelete={() => setFilteredCongressionalDistrict(null)}
              type="congressional_district"
              value={'CD ' + filteredCongressionalDistrict.name}
            />
          </Box>
        </Grow>
      )}
      {filteredStateSenateDistrict && setFilteredStateSenateDistrict && (
        <Grow in={!!filteredStateSenateDistrict}>
          <Box>
            <FilterLabel
              onDelete={() => setFilteredStateSenateDistrict(null)}
              type="state_leg_upper_district"
              value={'SD ' + filteredStateSenateDistrict.name}
            />
          </Box>
        </Grow>
      )}
      {filteredStateHouseDistrict && setFilteredStateHouseDistrict && (
        <Grow in={!!filteredStateHouseDistrict}>
          <Box>
            <FilterLabel
              onDelete={() => setFilteredStateHouseDistrict(null)}
              type="state_leg_lower_district"
              value={'HD ' + filteredStateHouseDistrict.name}
            />
          </Box>
        </Grow>
      )}
      {filteredDma && setFilteredDma && (
        <Grow in={!!filteredDma}>
          <Box>
            <FilterLabel
              onDelete={() => setFilteredDma(null)}
              type="dma_name"
              value={filteredDma.name}
            />
          </Box>
        </Grow>
      )}
      {filteredDateRange && setFilteredDateRange && (
        <Grow in={!!filteredDateRange}>
          <Box>
            <FilterLabel
              onDelete={() => setFilteredDateRange(null)}
              type="date"
              value={
                filteredDateRange.key === 'selection'
                  ? formatDateRange(filteredDateRange.startDate, filteredDateRange.endDate)
                  : filteredDateRange.key || ''
              }
            />
          </Box>
        </Grow>
      )}
    </Stack>
  );
}
